import React, { memo, useMemo, useState } from "react";
import {
  Box,
  MainCard,
  MainText,
  MainTitle,
  Pill,
  PressableOpacity,
} from "app/components/common";
import MultipleAvatars from "app/scenes/CurrentTrackScreen/TeamScreen/MultipleAvatars";
import normalize from "app/utils/normalize";
import theme from "app/Theme/Theme";
import ArrowRightSlider from "app/components/Icons/ArrowSlider/ArrowRightSlider";
import LinearGradient from "react-native-linear-gradient";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import TimerIcon from "app/components/Icons/TimerIcon";
import i18n from "@microdoing/shared/i18n";
import getRandomAvatarsFromNumber from "@microdoing/shared/utils/getRandomAvatarsFromNumber";
import { IconBox } from "../../Box/IconBox/IconBox";
import FlashIcon from "app/components/Icons/FlashIcon";
import CalendarIcon from "app/components/Icons/CalendarIcon";
import { useAddCalendarEvent } from "app/utils/useAddCalendarEvent";
import { useAddReminder } from "app/utils/useAddReminder";
import DateTimePicker from "react-native-modal-datetime-picker";
import displayNotifTime from "app/utils/displayNotifTime";
import FlashBoxIcon from "app/components/Icons/FlashBoxIcon";
import CheckBoxIcon from "app/components/Icons/CheckBoxIcon";
import ChangeIcon from "../../Icons/RefreshIcon";
import { useNavigation } from "@react-navigation/native";
import LightningIcon from "app/components/Icons/LightningIcon";
import { getUserCampaignStrategy } from "@microdoing/shared/services/userCampaign/selectors";
import { useSelector } from "react-redux";
import moment from "moment";

const ACTION_CARD_HEIGHT = normalize(110);

interface IAvatar {
  picture?: string;
  firstName?: string;
  _id?: string;
}

interface IProps {
  title: string;
  description: string;
  avatars: IAvatar[];
  category: string;
  gradientColor: string[];
  isSelected: boolean;
  isLockedForToday?: boolean;
  hasChangeAction?: boolean;
  canChangeAction?: boolean;
}

const ActionCard = ({
  title,
  description,
  avatars,
  category,
  gradientColor = [
    theme.colors["gradient.blueGradient.start"],
    theme.colors["gradient.blueGradient.end"],
  ],
  isSelected,
  isLockedForToday = false,
  hasChangeAction,
  canChangeAction,
}: IProps) => {
  const [height, setHeight] = useState(0);
  const [width, setWidth] = useState(0);
  const [openCalendar] = useAddCalendarEvent();
  const [
    isTimePickerVisible,
    openTimePicker,
    removeReminder,
    onConfirm,
    onCancel,
    hasChallengeReminder,
    challengeReminderTime,
  ] = useAddReminder();

  const navigation = useNavigation();

  const findDimensions = ({ height, width }) => {
    setWidth(width);
    setHeight(height);
  };

  const handleOnLayout = event => {
    findDimensions(event.nativeEvent.layout);
  };

  const handlePressReminder = onPress => () => {
    onPress();
    removeReminder();
  };

  const handleGoBackPress = () => {
    navigation.navigate("CancelAction");
  };

  const mainCardStyle = useMemo(() => {
    return {
      background: isWebDesktop
        ? `linear-gradient(109.23deg, ${gradientColor[0]} -101.65%, ${gradientColor[1]} 104.04%)`
        : null,
    };
  }, [gradientColor]);

  const randomAvatars = useMemo(
    () =>
      getRandomAvatarsFromNumber({
        baseNumber: 4,
      }),
    [],
  );

  const strategy = useSelector(getUserCampaignStrategy);

  return (
    <MainCard
      zIndex={25}
      paddingHorizontal="sm"
      elevation={1}
      shadowColor={{ phone: "neutral.900", smallWeb: "neutral.white" }}
      minHeight={
        isSelected || isLockedForToday ? undefined : ACTION_CARD_HEIGHT
      }
      maxHeight={
        isSelected || isLockedForToday ? undefined : ACTION_CARD_HEIGHT
      }
      shadowOffset={{
        width: 0,
        height: 5,
      }}
      shadowOpacity={0.25}
      onLayout={handleOnLayout}
      style={mainCardStyle}
      variant="actionCard"
    >
      <DateTimePicker
        customCancelButtonIOS={({ isDisabled, onPress, label }) => (
          <PressableOpacity onPress={handlePressReminder(onPress)}>
            <Box
              justifyContent="center"
              alignItems="center"
              backgroundColor="neutral.white"
              height={normalize(50)}
              borderRadius={12}
              marginBottom="s"
            >
              <MainText variant="l4-semibold" color="system.red.900">
                {i18n.t("delete")}
              </MainText>
            </Box>
          </PressableOpacity>
        )}
        cancelTextIOS={i18n.t("cancel")}
        headerTextIOS={i18n.t("choose-time")}
        confirmTextIOS={i18n.t("confirm")}
        isVisible={isTimePickerVisible}
        onConfirm={onConfirm}
        onCancel={onCancel}
        is24Hour={true}
        date={moment()
          .hour(challengeReminderTime?.hour || 14)
          .minute(challengeReminderTime?.minute || 0)
          .toDate()}
        mode="time"
      />
      {!isWebDesktop && (
        <LinearGradient
          pointerEvents="none"
          style={{
            position: "absolute",
            height: height,
            width: width,
            borderRadius: 8,
          }}
          start={{ x: 0, y: 4 }}
          end={{ x: 1, y: 0 }}
          colors={gradientColor}
        />
      )}
      <Box width="100%" alignItems="flex-start" flexDirection="row">
        {!isSelected && !isLockedForToday && (
          <Box
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            paddingVertical="sm"
          >
            {isLockedForToday ? <CheckBoxIcon /> : <FlashBoxIcon />}
            {!isLockedForToday && !isSelected && (
              <Box marginTop="sm" marginRight="xs">
                <MultipleAvatars
                  avatars={avatars?.length > 0 ? avatars : randomAvatars}
                  maxNumberOfAvatars={2}
                  size={20}
                />
              </Box>
            )}
          </Box>
        )}
        <Box
          flex={1}
          width="100%"
          marginLeft={isSelected ? "null" : "sm"}
          minHeight={
            isSelected || isLockedForToday ? undefined : ACTION_CARD_HEIGHT
          }
          maxHeight={
            isSelected || isLockedForToday ? undefined : ACTION_CARD_HEIGHT
          }
          paddingVertical="sm"
          paddingTop={isSelected || isLockedForToday ? "sm" : "null"}
          paddingBottom={isSelected ? "xs" : "sm"}
          justifyContent="space-between"
        >
          <Box
            flexDirection="row"
            width="100%"
            justifyContent="space-between"
            flex={1}
            alignItems="center"
          >
            {!isSelected && (
              <MainTitle
                variant="l6-bold"
                color="neutral.white"
                numberOfLines={2}
              >
                {title}
              </MainTitle>
            )}
            {isSelected && (
              <MainText color="neutral.white" variant="l4-medium">
                {title},
              </MainText>
            )}

            {!!category && !isLockedForToday && isSelected && (
              <Pill text={category} />
            )}
          </Box>
          <Box flexDirection="row" justifyContent="space-between" width="100%">
            <Box flex={1} width="100%" flexDirection="row">
              {!isSelected && (
                <MainText
                  lineHeight={0}
                  variant="l3-medium"
                  numberOfLines={isSelected ? 1 : 2}
                  color="neutral.white"
                >
                  {description}
                </MainText>
              )}
              {isSelected && (
                <Box
                  width="100%"
                  marginTop="xs"
                  flexDirection="row"
                  justifyContent="flex-start"
                  alignItems="center"
                >
                  <LightningIcon height={normalize(16)} width={normalize(16)} />
                  <MainText
                    marginLeft="xxs"
                    marginRight="s"
                    numberOfLines={1}
                    color="neutral.white"
                    variant="l6-bold"
                  >
                    {description}
                  </MainText>
                </Box>
              )}
            </Box>
          </Box>
        </Box>
        <Box alignSelf="flex-end" marginBottom="sm">
          {!hasChangeAction && !isSelected && (
            <ArrowRightSlider
              width={normalize(24)}
              height={normalize(24)}
              color={theme.colors["neutral.white"]}
            />
          )}
        </Box>
      </Box>
      {isSelected && !isLockedForToday && (
        <Box
          flex={1}
          marginTop={isSelected ? "null" : "s"}
          flexDirection="row"
          alignSelf="flex-start"
          justifyContent="space-between"
          width="100%"
        >
          <Box flexDirection="row" flex={1} width="100%">
            {isSelected && (
              <Box marginBottom="sm" flexDirection="row" width="100%">
                <PressableOpacity
                  onPress={openCalendar}
                  style={{ flexDirection: "row", alignItems: "center" }}
                >
                  <CalendarIcon
                    color={theme.colors["neutral.white"]}
                    width={normalize(16)}
                    height={normalize(16)}
                  />
                  <Box
                    marginLeft="xs"
                    borderBottomWidth={1}
                    borderBottomColor="neutral.white"
                  >
                    <MainText color="neutral.white" variant="l2-medium">
                      {i18n.t("add-calendar-event")}
                    </MainText>
                  </Box>
                </PressableOpacity>
                <PressableOpacity
                  onPress={openTimePicker}
                  style={{
                    marginLeft: theme.spacing.sm,
                    flexDirection: "row",
                    alignItems: "center",
                    flex: 1,
                  }}
                >
                  <TimerIcon
                    color={theme.colors["neutral.white"]}
                    width={normalize(16)}
                    height={normalize(16)}
                  />
                  <Box
                    marginLeft="xs"
                    borderBottomWidth={1}
                    borderBottomColor="neutral.white"
                  >
                    <MainText color="neutral.white" variant="l2-medium">
                      {hasChallengeReminder
                        ? i18n.t("reminder-set")
                        : i18n.t("add-reminder-challenge")}
                      {hasChallengeReminder && (
                        <MainText
                          variant="l2-medium"
                          textDecorationLine="none"
                          color="neutral.white"
                        >
                          {" "}
                          {displayNotifTime(challengeReminderTime)}
                        </MainText>
                      )}
                    </MainText>
                  </Box>
                </PressableOpacity>
                {strategy !== "ordered" && (
                  <>
                    {canChangeAction && (
                      <PressableOpacity
                        onPress={handleGoBackPress}
                        style={{
                          marginLeft: theme.spacing.s,
                          flexDirection: "row",
                          alignItems: "center",
                        }}
                      >
                        <ChangeIcon
                          color={theme.colors["neutral.white"]}
                          width={normalize(24)}
                          height={normalize(24)}
                        />
                      </PressableOpacity>
                    )}
                  </>
                )}
              </Box>
            )}
          </Box>
        </Box>
      )}
    </MainCard>
  );
};

export default memo(ActionCard);
